import { useEffect, useState } from "react";
import api from "../utils/api";

function getExitReason(trade) {
    const strat = (trade?.strategy || "").trim();
    if (!strat.startsWith("auto_exit_")) return "";

    // Allow formats like "auto_exit_take_profit:L2_eq_swing_target"
    const parts = strat.split(":");
    if (parts.length > 1 && parts[1]) {
        return parts[1];
    }

    // Fallback: strip the prefix so at least you see the decision
    return strat.replace("auto_exit_", "");
}

export default function AllTradesCard({ userId }) {
    const [trades, setTrades] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        if (!userId) return;
        setLoading(true);
        setError("");

        try {
            const { data } = await api.get("/api/trades/history", {
                params: { user_id: userId, page: 1, limit: 50 },
            });

            if (data.error) {
                setError(data.error);
                setTrades([]);
            } else {
                setTrades(data.trades || []);
            }
        } catch (e) {
            console.error("AllTradesCard load error", e);
            setError("Failed to load trade history.");
            setTrades([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [userId]);

    const downloadCsv = async () => {
        if (!userId) return;

        try {
            const res = await api.get("/api/trades/history", {
                params: { user_id: userId, format: "csv" },
                responseType: "blob",
            });

            const blob = new Blob([res.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);

            const safeId = (userId || "user").replace(/[@\s]/g, "_");

            const a = document.createElement("a");
            a.href = url;
            a.download = `mystockpicksai_trades_${safeId}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error("CSV download error", e);
            setError("Failed to download CSV.");
        }
    };

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3 mt-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">All Trades (site-wide)</h3>

                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        className="px-3 py-1 text-xs rounded bg-zinc-700 hover:bg-zinc-600"
                    >
                        Refresh
                    </button>
                    <button
                        onClick={downloadCsv}
                        className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-500 text-white"
                    >
                        Download CSV
                    </button>
                </div>
            </div>

            {userId && (
                <div className="text-xs text-zinc-500">
                    {userId}
                </div>
            )}

            {loading && (
                <div className="text-sm text-zinc-400">
                    Loading trade history�
                </div>
            )}

            {error && (
                <div className="text-xs text-red-400 break-words">{error}</div>
            )}

            {!loading && !error && trades.length === 0 && (
                <div className="text-sm text-zinc-400">
                    No trades logged yet for this account.
                </div>
            )}

            {trades.length > 0 && (
                <div className="max-h-64 overflow-y-auto text-xs space-y-2">
                    {trades.map((t) => {
                        const exitReason = getExitReason(t);
                        const statusOk =
                            (t.status || "").toLowerCase() === "success";

                        return (
                            <div
                                key={t.id}
                                className="flex justify-between border-b border-zinc-800 pb-1 last:border-0"
                            >
                                <div>
                                    <div className="text-zinc-100">
                                        {t.asset_type === "option"
                                            ? `${t.symbol} (${t.option_symbol || "opt"})`
                                            : t.symbol}
                                    </div>

                                    <div className="text-zinc-400">
                                        {t.side} � {t.broker} �{" "}
                                        {t.strategy || t.source || "�"}
                                    </div>

                                    {exitReason && (
                                        <div className="text-[10px] text-amber-300">
                                            Exit reason: {exitReason}
                                        </div>
                                    )}
                                </div>

                                <div className="text-right">
                                    <div>Qty: {t.qty}</div>

                                    <div
                                        className={
                                            statusOk
                                                ? "text-emerald-400"
                                                : "text-red-400"
                                        }
                                    >
                                        {t.status}
                                    </div>

                                    <div className="text-[10px] text-zinc-500">
                                        {t.created_at}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
